import React, { useMemo } from "react";
import { IconButton } from "@mui/material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";

import CustomTooltip from "components/common/Tooltip";

import { hasPermission } from "hooks/usePermission";
import UserInfo from "./UserInfo";

function UserItem(props) {
  const {
    user,
    groupName,
    roleName,
    loggedInUser,
    setEditAbleUser,
    setAddorEditUser,
    handleRemoveUser,
    handleMoveUser = () => {},
  } = props;

  const { hasRestrictedAccess, isCurrentUser } = useMemo(() => {
    const restrictedRoles = ["CustomerAdmin", "CustomerEmployee"];
    return {
      hasRestrictedAccess:
        loggedInUser?.permissions?.some((permission) => restrictedRoles.includes(permission)) && user?.mainUser,
      isCurrentUser: loggedInUser?.email === user?.email,
    };
  }, [loggedInUser?.permissions, loggedInUser?.email, user?.mainUser, user?.email]);

  // Gate edit/delete by the active context: edge employees use edgeUsers.*,
  // customer users use userManagement.*.
  const isEdgeContext = loggedInUser?.type === "edge";
  const canEditUser = isEdgeContext ? hasPermission("edgeUsers.update") : hasPermission("userManagement.update");
  const canDeleteUser = isEdgeContext ? hasPermission("edgeUsers.deactivate") : hasPermission("userManagement.delete");
  // Moving a user between groups always goes through the guarded members endpoint, so it is gated on
  // groups.assign_member regardless of context. Preserve the existing self / main-user guards: the
  // account owner can't leave Main and a user can't reassign themselves (the backend enforces this
  // too, but hiding the control avoids a guaranteed-failure action).
  const canMoveUser = hasPermission("groups.assign_member") && !user?.mainUser && !isCurrentUser;

  return (
    <div className="flex justify-between items-center p-1">
      <UserInfo user={user} groupName={groupName} roleName={roleName} />
      {(!hasRestrictedAccess || isCurrentUser) && (
        <div className="flex gap-05 items-center">
          {canEditUser && (
            <IconButton
              aria-label="Edit user"
              onClick={() => {
                setEditAbleUser(user);
                setAddorEditUser({
                  open: true,
                  mode: "editUser",
                });
              }}
              size="small"
              className="edit-delete-icon-size"
            >
              <ModeEditOutlineOutlinedIcon />
            </IconButton>
          )}

          {canMoveUser && (
            <CustomTooltip content="Move to group">
              <IconButton
                aria-label="Move to group"
                onClick={() => handleMoveUser(user)}
                size="small"
                className="edit-delete-icon-size"
              >
                <DriveFileMoveOutlinedIcon />
              </IconButton>
            </CustomTooltip>
          )}

          {canDeleteUser && (
            <IconButton
              aria-label="Delete user"
              disabled={user?.mainUser || isCurrentUser}
              size="small"
              className="edit-delete-icon-size"
            >
              <DeleteOutlineIcon onClick={() => handleRemoveUser(user)} />
            </IconButton>
          )}
        </div>
      )}
    </div>
  );
}

export default UserItem;
